import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import './styles.css';

const CLASS_PREFIX = 'idpHighlightSelect';
const PLACEHOLDER_VALUE = '';

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const toOption = (option) => ({
	value: option.value,
	label: option.text,
	isCustom: !!option.isCustom,
});

const CheckmarkOption = (props) => {
	const isPlaceholder = props.data.value === PLACEHOLDER_VALUE;
	return (
		<components.Option {...props}>
			<span className={`${CLASS_PREFIX}__check`} aria-hidden="true">
				{props.isSelected ? '✓' : ''}
			</span>
			<span
				className={joinClasses(`${CLASS_PREFIX}__optionLabel`, isPlaceholder && `${CLASS_PREFIX}__optionLabel--muted`)}
			>
				{props.data.label}
			</span>
		</components.Option>
	);
};

const DropdownIndicator = (props) => (
	<components.DropdownIndicator {...props}>
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
			<polyline
				points="6 9 12 15 18 9"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</components.DropdownIndicator>
);

export default function GroupHighlightSelectField({
	groupHighlights,
	containerClassName = '',
	// eslint-disable-next-line no-unused-vars
	selectClassName = '',
	labelClassName = '',
}) {
	const { t } = useTranslation();

	const groupedOptions = useMemo(() => {
		if (!groupHighlights?.options?.length) return [];
		const placeholder = { value: PLACEHOLDER_VALUE, label: t('Select group to highlight') };
		const standard = groupHighlights.options.filter((o) => !o.isCustom).map(toOption);
		const custom = groupHighlights.options.filter((o) => o.isCustom).map(toOption);
		const groups = [{ options: [placeholder, ...standard] }];
		if (custom.length) groups.push({ label: t('Custom groups'), options: custom });
		return groups;
	}, [groupHighlights, t]);

	const selectedOption = useMemo(() => {
		const flat = groupedOptions.flatMap((g) => g.options);
		return flat.find((o) => o.value === (groupHighlights?.selected ?? PLACEHOLDER_VALUE)) || null;
	}, [groupedOptions, groupHighlights]);

	if (!groupHighlights || !groupHighlights.options.length) {
		return null;
	}

	const handleChange = (option) => {
		const value = option?.value ?? PLACEHOLDER_VALUE;
		groupHighlights.selected = value;
		groupHighlights.onChange({ target: { value } });
	};

	return (
		<div
			className={joinClasses(
				'idp-settings-advanced__field idp-settings-advanced__field--constrained',
				containerClassName,
			)}
		>
			<span className={labelClassName || undefined}>{groupHighlights.header}:</span>
			<Select
				unstyled
				classNamePrefix={CLASS_PREFIX}
				className={`${CLASS_PREFIX}__container`}
				options={groupedOptions}
				value={selectedOption}
				onChange={handleChange}
				isSearchable={false}
				isClearable={false}
				menuPlacement="auto"
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				menuPosition="fixed"
				components={{ Option: CheckmarkOption, DropdownIndicator, IndicatorSeparator: () => null }}
				aria-label={groupHighlights.header}
			/>
		</div>
	);
}
